class ClayProductArchive extends HTMLElement {
  #searchTimer = null;
  #priceCommitTimer = null;
  #requestController = null;
  #infiniteController = null;
  #featuredController = null;
  #metadataController = null;
  #observer = null;
  #backendUrl = null;
  #loadingMore = false;
  #selectedCategories = new Set();
  #selectedBrands = new Set();
  #selectedTags = new Set();
  #availabilityMode = 'all';
  #priceDrag = null;
  #featuredIndex = 0;
  #metadata = [];
  #metadataMap = new Map();
  #metadataKey = '';
  #metadataReady = false;
  #searchTerm = '';
  #categoryLabelCache = new Map();
  #hasLoadedMetadata = false;

  connectedCallback() {
    this.#restoreGridDensity();
    this.#readClientStateFromUrl();

    this.addEventListener('click', this.#handleClick);
    this.addEventListener('change', this.#handleChange);
    this.addEventListener('input', this.#handleInput);
    this.addEventListener('submit', this.#handleSubmit);
    this.addEventListener('pointerdown', this.#handlePointerDown);
    this.addEventListener('pointermove', this.#handlePointerMove);
    this.addEventListener('pointerup', this.#handlePointerUp);
    this.addEventListener('pointercancel', this.#handlePointerUp);
    this.addEventListener('keydown', this.#handleKeyDown);
    document.addEventListener('click', this.#handleDocumentClick);
    window.addEventListener('popstate', this.#handlePopState);

    if (this.dataset.needsHydration === 'true') {
      this.#replaceResults(this.#urlFromVisibleState(), { history: false });
    } else {
      this.#backendUrl = this.#urlFromCurrentContext();
      this.#afterContentUpdate({ reloadMetadata: true });
    }
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('change', this.#handleChange);
    this.removeEventListener('input', this.#handleInput);
    this.removeEventListener('submit', this.#handleSubmit);
    this.removeEventListener('pointerdown', this.#handlePointerDown);
    this.removeEventListener('pointermove', this.#handlePointerMove);
    this.removeEventListener('pointerup', this.#handlePointerUp);
    this.removeEventListener('pointercancel', this.#handlePointerUp);
    this.removeEventListener('keydown', this.#handleKeyDown);
    document.removeEventListener('click', this.#handleDocumentClick);
    window.removeEventListener('popstate', this.#handlePopState);

    clearTimeout(this.#searchTimer);
    clearTimeout(this.#priceCommitTimer);

    this.#requestController?.abort();
    this.#infiniteController?.abort();
    this.#featuredController?.abort();
    this.#metadataController?.abort();
    this.#observer?.disconnect();
  }

  get mode() {
    return this.dataset.mode || 'all';
  }

  get browseUrl() {
    return new URL(this.dataset.browseUrl || '/collections/all', window.location.origin);
  }

  get searchUrl() {
    return new URL(this.dataset.searchUrl || '/search', window.location.origin);
  }

  get categoryHandle() {
    return (this.dataset.categoryHandle || '').trim();
  }

  get categoryPathHandles() {
    return this.dataset.categoryPathHandles || '|';
  }

  get categoryScopeHandles() {
    return new Set(
      (this.dataset.categoryScopeHandles || '')
        .split('|')
        .map((value) => value.trim())
        .filter(Boolean)
    );
  }

  get categoryAggregate() {
    return this.dataset.categoryAggregate === 'true';
  }

  get categoryExclusions() {
    return new Set(
      (this.dataset.categoryExclusions || '')
        .split(',')
        .map((value) => this.#normalise(value))
        .filter(Boolean)
    );
  }

  get brandName() {
    return (this.dataset.brandName || '').trim();
  }

  get brandId() {
    return (this.dataset.brandId || '').trim();
  }

  get brandProductIds() {
    return new Set(
      (this.dataset.brandProductIds || '')
        .split('|')
        .map((value) => value.trim())
        .filter(Boolean)
    );
  }

  get hideUnavailable() {
    return this.dataset.hideUnavailable === 'true';
  }

  get metadataSectionId() {
    return (this.dataset.metadataSectionId || 'clay-product-archive-data').trim();
  }

  get featuredEnabled() {
    return this.dataset.featuredEnabled === 'true';
  }

  get featuredLimit() {
    return Math.max(1, Number(this.dataset.featuredLimit || 4));
  }

  get featuredTagHandle() {
    return (this.dataset.featuredTagHandle || 'featured').trim();
  }

  get requiredProductTag() {
    return (this.dataset.requiredProductTag || '').trim();
  }

  #renderBrandDebug(error = null) {
    if (this.mode !== 'brand' || this.dataset.brandDebugEnabled !== 'true') return;

    const output = this.querySelector('[data-brand-debug-client]');
    if (!output) return;

    const referencedProducts = this.brandProductIds;
    const loadedItems = [...this.querySelectorAll('[data-archive-grid] > .clay-archive-grid__item')];
    const scopedMetadata = this.#metadataReady ? this.#metadataInArchiveScope() : [];
    const currentBrand = this.#normalise(this.brandName);

    const loadedSample = loadedItems.slice(0, 12).map((item) => ({
      id: String(item.dataset.productId || ''),
      vendor: item.dataset.productVendor || '',
      vendorNormalized: this.#normalise(item.dataset.productVendor || ''),
      ccBrand: item.dataset.brandName || '',
      ccBrandNormalized: this.#normalise(item.dataset.brandName || ''),
      ccBrandId: item.dataset.brandId || '',
      referenced: referencedProducts.has(String(item.dataset.productId || '')),
      scopeMatch: this.#itemMatchesArchiveScope(item),
      hidden: item.hidden,
    }));

    const metadataSample = this.#metadata.slice(0, 20).map((meta) => ({
      id: String(meta.id || ''),
      vendor: meta.vendor || '',
      vendorNormalized: this.#normalise(meta.vendor || ''),
      ccBrand: meta.brand || '',
      ccBrandNormalized: this.#normalise(meta.brand || ''),
      ccBrandId: meta.brandId || '',
      referenced: referencedProducts.has(String(meta.id || '')),
    }));

    const summary = {
      mode: this.mode,
      brandName: this.brandName,
      brandNameNormalized: currentBrand,
      brandId: this.brandId,
      brandBackendStrategy: this.dataset.brandBackendStrategy || null,
      brandProductDeclaredCountLiquid: Number(this.dataset.brandProductDeclaredCount || 0),
      brandProductCountLiquid: Number(this.dataset.brandProductCount || 0),
      brandProductIdsParsedCount: referencedProducts.size,
      firstReferencedProductIds: [...referencedProducts].slice(0, 25),
      backendUrl: this.#backendUrl?.toString() || null,
      searchTerm: this.#searchTerm,
      metadataReady: this.#metadataReady,
      metadataTotal: this.#metadata.length,
      metadataInBrandScope: scopedMetadata.length,
      loadedGridItems: loadedItems.length,
      loadedGridItemsMatchingScope: loadedItems.filter((item) => this.#itemMatchesArchiveScope(item)).length,
      currentUrl: window.location.href,
      error: error ? String(error?.stack || error?.message || error) : null,
      loadedSample,
      metadataSample,
    };

    output.textContent = JSON.stringify(summary, null, 2);
  }

  #normalise(value) {
    return String(value || '')
      .normalize('NFKD')
      .toLocaleLowerCase()
      .replace(/&amp;/g, '&')
      .replace(/[^a-z0-9]+/g, '');
  }

  #hasExactTag(tags, wantedTag) {
    const wanted = this.#normalise(wantedTag);
    if (!wanted) return true;

    const values = Array.isArray(tags) ? tags : String(tags || '').split('||');
    return values.some((tag) => this.#normalise(tag) === wanted);
  }

  #isHiddenCategory(category) {
    const handle = this.#normalise(category?.handle);
    const title = this.#normalise(category?.title);

    return (
      ['all', 'allproducts', 'frontpage'].includes(handle) ||
      title === 'allproducts' ||
      this.categoryExclusions.has(handle) ||
      this.categoryExclusions.has(title)
    );
  }

  #withVisibleCategories(product) {
    return {
      ...product,
      categories: (product.categories || []).filter((category) => !this.#isHiddenCategory(category)),
    };
  }

  #readClientStateFromUrl() {
    const url = new URL(window.location.href);

    this.#searchTerm = url.searchParams.get('q') || '';
    this.#availabilityMode = url.searchParams.get('cc_availability') || 'all';
    this.#selectedCategories = new Set(
      url.searchParams
        .getAll('cc_category')
        .filter((handle) => handle && !this.#isHiddenCategory({ handle }))
    );
    this.#selectedBrands = new Set(
      url.searchParams.getAll('cc_brand').filter(Boolean)
    );
    this.#selectedTags = new Set(
      url.searchParams.getAll('cc_tag').filter(Boolean)
    );
  }

  #urlFromCurrentContext() {
    const visible = new URL(window.location.href);

    if (this.mode === 'brand') {
      const target = new URL(this.browseUrl);

      for (const [key, value] of visible.searchParams.entries()) {
        if (key.startsWith('filter.') || key === 'sort_by') {
          target.searchParams.append(key, value);
        }
      }

      return this.#applyScope(target);
    }

    const current = new URL(visible);

    current.searchParams.delete('cc_availability');
    current.searchParams.delete('cc_category');
    current.searchParams.delete('cc_brand');

    if (this.mode === 'category') {
      current.pathname = this.browseUrl.pathname;
    }

    return this.#applyScope(current);
  }

  #urlFromVisibleState() {
    const visible = new URL(window.location.href);
    const term = (visible.searchParams.get('q') || '').trim();
    const target = term.length >= 3
      ? new URL(this.searchUrl)
      : new URL(this.browseUrl);

    if (term.length >= 3 || this.mode !== 'brand') {
      target.search = '';
    }

    for (const [key, value] of visible.searchParams.entries()) {
      if (
        key.startsWith('filter.') ||
        key === 'sort_by' ||
        (key === 'q' && term.length >= 3)
      ) {
        target.searchParams.append(key, value);
      }
    }

    if (term.length >= 3) {
      target.searchParams.set('q', term);
      target.searchParams.set('type', 'product');
    }

    return this.#applyScope(target);
  }

  #applyScope(url) {
    const scoped = new URL(url);

    scoped.searchParams.delete('section_id');
    scoped.searchParams.delete('sections');
    scoped.searchParams.delete('page');
    scoped.searchParams.delete('cc_availability');
    scoped.searchParams.delete('cc_category');
    scoped.searchParams.delete('cc_brand');
    scoped.searchParams.delete('cc_tag');

    return scoped;
  }

  #buildUrlFromForm(form) {
    const action = this.#backendUrl
      ? new URL(this.#backendUrl)
      : new URL(form.action, window.location.origin);
    const formData = new FormData(form);
    const preservedQuery = this.#searchTerm.trim();

    action.search = '';

    if (preservedQuery.length >= 3) {
      action.pathname = this.searchUrl.pathname;
      action.searchParams.set('q', preservedQuery);
      action.searchParams.set('type', 'product');
    } else if (this.mode === 'brand') {
      const browse = new URL(this.browseUrl);
      action.pathname = browse.pathname;
      action.search = browse.search;
    } else if (this.mode === 'category') {
      action.pathname = this.browseUrl.pathname;
    }

    for (const [key, rawValue] of formData.entries()) {
      const value = String(rawValue).trim();

      if (!value) continue;
      if (key.startsWith('cc_')) continue;
      if (key === 'q' || key === 'type') continue;

      action.searchParams.append(key, value);
    }

    form.querySelectorAll('[data-price-range]').forEach((range) => {
      const min = range.querySelector('[data-price-min-input]');
      const max = range.querySelector('[data-price-max-input]');
      const floor = Number(range.dataset.rangeMin || 0);
      const ceiling = Number(range.dataset.rangeMax || 0);

      if (min && Number(min.value) <= floor) {
        action.searchParams.delete(min.name);
      }

      if (max && Number(max.value) >= ceiling) {
        action.searchParams.delete(max.name);
      }
    });

    action.searchParams.delete('page');

    return this.#applyScope(action);
  }

  #buildSearchUrl(term) {
    const form = this.querySelector('[data-filter-form]');
    const current = form
      ? this.#buildUrlFromForm(form)
      : (this.#backendUrl ? new URL(this.#backendUrl) : new URL(this.browseUrl));

    const target = term.length >= 3
      ? new URL(this.searchUrl)
      : new URL(this.browseUrl);

    target.search = '';

    for (const [key, value] of current.searchParams.entries()) {
      if (!key.startsWith('filter.') && key !== 'sort_by') continue;
      target.searchParams.append(key, value);
    }

    if (term.length >= 3) {
      target.searchParams.set('q', term);
      target.searchParams.set('type', 'product');
    }

    return this.#applyScope(target);
  }

  #handleSubmit = (event) => {
    const form = event.target.closest?.('[data-filter-form]');

    if (!form) return;

    event.preventDefault();
    this.#replaceResults(this.#buildUrlFromForm(form));
  };

  #handleInput = (event) => {
    const target = event.target;

    if (!target.matches?.('[data-archive-search]')) return;

    clearTimeout(this.#searchTimer);

    const term = target.value.trim();
    this.#searchTerm = term;

    if (term.length > 0 && term.length < 3) return;

    this.#searchTimer = window.setTimeout(() => {
      this.#replaceResults(this.#buildSearchUrl(term));
    }, 450);
  };

  #handleChange = (event) => {
    const target = event.target;

    if (target.matches?.('[data-availability-filter]')) {
      const value = target.value || 'all';

      if (value === 'all') {
        this.#availabilityMode = 'all';
      } else if (target.checked) {
        this.#availabilityMode = value;
      } else if (this.#availabilityMode === value) {
        this.#availabilityMode = 'all';
      }

      this.#restoreClientFilterControls();
      this.#applyClientFilters();
      this.#pushClientState();
      this.#ensureEnoughVisibleProducts();
      return;
    }

    if (target.matches?.('[data-category-filter]')) {
      if (target.checked) {
        this.#selectedCategories.add(target.value);
      } else {
        this.#selectedCategories.delete(target.value);
      }

      this.#restoreClientFilterControls();
      this.#applyClientFilters();
      this.#pushClientState();
      this.#ensureEnoughVisibleProducts();
      return;
    }

    if (target.matches?.('[data-brand-filter]')) {
      if (target.checked) {
        this.#selectedBrands.add(target.value);
      } else {
        this.#selectedBrands.delete(target.value);
      }

      this.#restoreClientFilterControls();
      this.#applyClientFilters();
      this.#pushClientState();
      this.#ensureEnoughVisibleProducts();
      return;
    }

    if (target.matches?.('[data-tag-filter]')) {
      if (target.checked) {
        this.#selectedTags.add(target.value);
      } else {
        this.#selectedTags.delete(target.value);
      }

      this.#restoreClientFilterControls();
      this.#applyClientFilters();
      this.#pushClientState();
      this.#ensureEnoughVisibleProducts();
      return;
    }

    const form = target.closest?.('[data-filter-form]');

    if (!form) return;

    this.#replaceResults(this.#buildUrlFromForm(form));
  };

  #handleClick = (event) => {
    const gridDensityOption = event.target.closest?.('[data-grid-density-option]');

    if (gridDensityOption) {
      this.#setGridDensity(gridDensityOption.dataset.gridDensityOption);
      return;
    }

    const clayEditSummary = event.target.closest?.('[data-clay-edit-filter-summary]');

    if (clayEditSummary) {
      const currentDetails = clayEditSummary.closest('details');
      this.querySelectorAll('.clay-edit-hero__filter[open]').forEach((details) => {
        if (details !== currentDetails) details.removeAttribute('open');
      });
      return;
    }

    const clayEditApply = event.target.closest?.('[data-clay-edit-apply]');

    if (clayEditApply) {
      event.preventDefault();
      this.querySelectorAll('.clay-edit-hero__filter[open]').forEach((details) => {
        details.removeAttribute('open');
      });
      this.querySelector('.clay-product-archive__inner')?.scrollIntoView({
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        block: 'start',
      });
      return;
    }

    const toggle = event.target.closest?.('[data-archive-filter-toggle]');

    if (toggle) {
      const nextOpen = this.dataset.filtersOpen !== 'true';
      this.dataset.filtersOpen = String(nextOpen);
      toggle.setAttribute('aria-expanded', String(nextOpen));
      return;
    }

    const clear = event.target.closest?.('[data-archive-clear]');

    if (clear) {
      event.preventDefault();
      this.#availabilityMode = 'all';
      this.#selectedCategories.clear();
      this.#selectedBrands.clear();
      this.#selectedTags.clear();
      this.#replaceResults(this.#applyScope(new URL(this.browseUrl)));
      return;
    }

    const priceTrack = event.target.closest?.('[data-price-track]');

    if (priceTrack && !event.target.closest('[data-price-handle]')) {
      const range = priceTrack.closest('[data-price-range]');
      this.#setPriceFromPointer(range, event.clientX);
      this.#commitPriceRange(range);
      return;
    }

    const prev = event.target.closest?.('[data-featured-prev]');

    if (prev) {
      this.#moveFeatured(-1);
      return;
    }

    const next = event.target.closest?.('[data-featured-next]');

    if (next) {
      this.#moveFeatured(1);
    }
  };

  #handlePointerDown = (event) => {
    const handle = event.target.closest?.('[data-price-handle]');

    if (!handle) return;

    event.preventDefault();

    const range = handle.closest('[data-price-range]');

    if (!range) return;

    this.#priceDrag = {
      range,
      role: handle.dataset.priceHandle,
      pointerId: event.pointerId,
      handle,
    };

    handle.classList.add('is-dragging');

    try {
      handle.setPointerCapture(event.pointerId);
    } catch {}

    this.#setPriceValue(range, this.#priceDrag.role, event.clientX);
  };

  #handlePointerMove = (event) => {
    if (!this.#priceDrag || this.#priceDrag.pointerId !== event.pointerId) return;

    event.preventDefault();

    this.#setPriceValue(
      this.#priceDrag.range,
      this.#priceDrag.role,
      event.clientX
    );
  };

  #handlePointerUp = (event) => {
    if (!this.#priceDrag || this.#priceDrag.pointerId !== event.pointerId) return;

    const { range, handle } = this.#priceDrag;

    handle.classList.remove('is-dragging');

    try {
      handle.releasePointerCapture(event.pointerId);
    } catch {}

    this.#priceDrag = null;
    this.#commitPriceRange(range);
  };

  #handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      const openFilter = event.target.closest?.('.clay-edit-hero__filter[open]');
      if (openFilter) {
        openFilter.removeAttribute('open');
        openFilter.querySelector('summary')?.focus();
        event.preventDefault();
        return;
      }
    }

    const handle = event.target.closest?.('[data-price-handle]');

    if (!handle) return;

    const range = handle.closest('[data-price-range]');
    const role = handle.dataset.priceHandle;
    const floor = Number(range.dataset.rangeMin || 0);
    const ceiling = Number(range.dataset.rangeMax || 0);
    const input = range.querySelector(
      role === 'min' ? '[data-price-min-input]' : '[data-price-max-input]'
    );

    if (!input) return;

    let value = Number(input.value);
    const step = Math.max(1, Math.round((ceiling - floor) / 100));

    if (event.key === 'ArrowLeft' || event.key === 'ArrowDown') {
      value -= step;
    } else if (event.key === 'ArrowRight' || event.key === 'ArrowUp') {
      value += step;
    } else if (event.key === 'Home') {
      value = floor;
    } else if (event.key === 'End') {
      value = ceiling;
    } else {
      return;
    }

    event.preventDefault();
    this.#setPriceNumericValue(range, role, value);
    this.#schedulePriceCommit(range);
  };

  #handleDocumentClick = (event) => {
    if (event.target.closest?.('.clay-edit-hero__filter')) return;

    this.querySelectorAll('.clay-edit-hero__filter[open]').forEach((details) => {
      details.removeAttribute('open');
    });
  };

  #handlePopState = () => {
    this.#readClientStateFromUrl();
    this.#replaceResults(this.#urlFromVisibleState(), { history: false });
  };

  #restoreGridDensity() {
    let density = 'default';

    try {
      const storedDensity = window.localStorage.getItem('clay-archive-grid-density');
      if (storedDensity === 'reduced') density = 'reduced';
    } catch (_error) {
      // Storage can be unavailable in privacy-restricted browsing contexts.
    }

    this.#setGridDensity(density, { persist: false });
  }

  #setGridDensity(density, { persist = true } = {}) {
    const nextDensity = density === 'reduced' ? 'reduced' : 'default';
    this.dataset.gridDensity = nextDensity;

    this.querySelectorAll('[data-grid-density-option]').forEach((button) => {
      button.setAttribute(
        'aria-pressed',
        String(button.dataset.gridDensityOption === nextDensity)
      );
    });

    if (!persist) return;

    try {
      window.localStorage.setItem('clay-archive-grid-density', nextDensity);
    } catch (_error) {
      // The visual preference still applies for the current page.
    }
  }

  async #fetchSection(url, controller, sectionId = this.dataset.renderSectionId) {
    const requestUrl = new URL(url);

    requestUrl.searchParams.set('section_id', sectionId);

    const response = await fetch(requestUrl, {
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
      },
      signal: controller.signal,
    });

    if (!response.ok) {
      throw new Error(`Archive request failed: ${response.status}`);
    }

    const html = await response.text();
    return new DOMParser().parseFromString(html, 'text/html');
  }

  async #replaceResults(url, { history = true } = {}) {
    const scopedUrl = this.#applyScope(url);
    const hydrateSidebar = this.dataset.needsHydration === 'true';

    this.#rememberDynamicFacetLabels();

    this.#observer?.disconnect();
    this.#requestController?.abort();
    this.#infiniteController?.abort();
    this.#featuredController?.abort();
    this.#metadataController?.abort();

    const requestController = new AbortController();
    this.#requestController = requestController;
    this.#loadingMore = false;
    this.#metadataReady = false;
    this.#metadata = [];
    this.#metadataMap = new Map();
    this.#metadataKey = '';

    this.classList.add('is-loading');
    this.setAttribute('aria-busy', 'true');

    try {
      const documentFragment = await this.#fetchSection(
        scopedUrl,
        requestController
      );

      const nextResults = documentFragment.querySelector('[data-archive-results-content]');
      const currentResults = this.querySelector('[data-archive-results-content]');

      if (!nextResults || !currentResults) {
        throw new Error('Rendered Clay archive results were not found.');
      }

      if (hydrateSidebar) {
        const nextSidebar = documentFragment.querySelector('[data-archive-sidebar-content]');
        const currentSidebar = this.querySelector('[data-archive-sidebar-content]');

        if (nextSidebar && currentSidebar) {
          currentSidebar.replaceWith(nextSidebar);
        }
      }

      currentResults.replaceWith(nextResults);

      this.#backendUrl = scopedUrl;
      this.dataset.needsHydration = 'false';

      this.#restoreSearchControl();
      this.#afterContentUpdate({ reloadMetadata: true });

      if (history) {
        this.#syncVisibleUrl(scopedUrl);
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('[Clay product archive]', error);
      }
    } finally {
      if (this.#requestController === requestController) {
        this.classList.remove('is-loading');
        this.removeAttribute('aria-busy');
      }
    }
  }

  #restoreSearchControl() {
    const input = this.querySelector('[data-archive-search]');
    if (input) input.value = this.#searchTerm;

    const hiddenQuery = this.querySelector('[data-filter-form] input[type="hidden"][name="q"]');
    if (hiddenQuery) hiddenQuery.value = this.#searchTerm;
  }

  #rememberDynamicFacetLabels() {
    this.querySelectorAll('[data-category-option]').forEach((option) => {
      const handle = option.dataset.categoryHandle || '';
      const title = option.dataset.categoryTitle ||
        option.querySelector('.clay-archive-filter__label')?.textContent?.trim() || '';

      if (handle && title) this.#categoryLabelCache.set(handle, title);
    });
  }

  #afterContentUpdate({ reloadMetadata = false } = {}) {
    this.#renderBrandDebug();
    this.#restoreSearchControl();
    this.#restoreCategoryPathMarkers();
    this.#restoreClientFilterControls();
    this.#refreshPriceRanges();
    this.#applyClientFilters();
    this.#setupInfiniteScroll();
    this.#loadFeatured();

    if (reloadMetadata) {
      this.#loadAllMetadata();
    } else {
      this.#updateFacets();
    }

    this.#ensureEnoughVisibleProducts();
  }

  #restoreCategoryPathMarkers() {
    const pathHandles = this.categoryPathHandles;

    this.querySelectorAll('[data-category-option]').forEach((option) => {
      const handle = option.dataset.categoryHandle || '';
      option.dataset.categoryPath = String(
        Boolean(handle) && pathHandles.includes(`|${handle}|`)
      );
    });
  }

  #restoreClientFilterControls() {
    this.querySelectorAll('[data-availability-filter]').forEach((input) => {
      input.checked = this.#availabilityMode === 'all'
        ? input.value === 'all'
        : input.value === this.#availabilityMode;
    });

    this.querySelectorAll('[data-category-filter]').forEach((input) => {
      input.checked = this.#selectedCategories.has(input.value);
    });

    this.querySelectorAll('[data-brand-filter]').forEach((input) => {
      input.checked = this.#selectedBrands.has(input.value);
    });

    this.querySelectorAll('[data-tag-filter]').forEach((input) => {
      input.checked = [...this.#selectedTags].some(
        (tag) => this.#normalise(tag) === this.#normalise(input.value)
      );
    });
  }

  #itemMatchesArchiveScope(item) {
    if (!item) return false;

    if (this.hideUnavailable) {
      const status = item.dataset.stockStatus || 'out_of_stock';
      const price = Number(item.dataset.productPrice || 0);

      if (status === 'out_of_stock' || price <= 0) return false;
    }

    if (
      this.requiredProductTag &&
      !this.#hasExactTag(item.dataset.productTags || '', this.requiredProductTag)
    ) {
      return false;
    }

    if (this.mode === 'brand') {
      const currentBrand = this.#normalise(this.brandName);
      const productVendor = this.#normalise(item.dataset.productVendor || '');
      const productBrand = this.#normalise(item.dataset.brandName || '');

      if (currentBrand && currentBrand !== productVendor && currentBrand !== productBrand) {
        return false;
      }
    }

    if (this.mode === 'category' && this.categoryScopeHandles.size > 0) {
      const categories = item.dataset.categoryHandles || '|';
      const belongsToScope = [...this.categoryScopeHandles].some((handle) =>
        categories.includes(`|${handle}|`)
      );

      if (!belongsToScope) return false;
    }

    return true;
  }

  #applyClientFilters({ updateFacets = true } = {}) {
    const grid = this.querySelector('[data-archive-grid]');

    if (grid) {
      grid.querySelectorAll(':scope > .clay-archive-grid__item').forEach((item) => {
        const status = item.dataset.stockStatus || 'out_of_stock';
        const categories = item.dataset.categoryHandles || '|';
        const brand = item.dataset.brandName || '';
        const tags = item.dataset.productTags || '';

        const scopeMatches = this.#itemMatchesArchiveScope(item);
        const availabilityMatches =
          this.#availabilityMode === 'all' || status === this.#availabilityMode;

        const categoryMatches =
          this.#selectedCategories.size === 0 ||
          [...this.#selectedCategories].some((handle) =>
            categories.includes(`|${handle}|`)
          );

        const brandMatches =
          this.#selectedBrands.size === 0 ||
          [...this.#selectedBrands].some((selected) =>
            this.#normalise(selected) === this.#normalise(brand)
          );

        const tagMatches =
          this.#selectedTags.size === 0 ||
          [...this.#selectedTags].some((selected) =>
            this.#hasExactTag(tags, selected)
          );

        item.hidden = !(scopeMatches && availabilityMatches && categoryMatches && brandMatches && tagMatches);
      });
    }

    this.#updateClientEmptyState();

    if (updateFacets) this.#updateFacets();
  }

  #updateClientEmptyState() {
    const content = this.querySelector('[data-archive-results-content]');
    const grid = content?.querySelector('[data-archive-grid]');
    const template = content?.querySelector('[data-client-empty-template]');
    const currentEmpty = content?.querySelector('[data-client-empty]');

    if (!content || !grid || !template) return;

    const hasVisibleProducts = Boolean(
      grid.querySelector(':scope > .clay-archive-grid__item:not([hidden])')
    );
    const hasNextPage = Boolean(
      content.querySelector('[data-infinite-sentinel]')?.dataset.nextUrl
    );

    if (hasVisibleProducts || hasNextPage || this.#loadingMore) {
      currentEmpty?.remove();
      return;
    }

    if (!currentEmpty) {
      content.append(template.content.cloneNode(true));
    }
  }

  async #loadAllMetadata() {
    if (!this.#backendUrl) return;

    const baseUrl = this.#applyScope(this.#backendUrl);
    const key = `${baseUrl.pathname}?${baseUrl.searchParams.toString()}`;

    if (this.#metadataReady && this.#metadataKey === key) {
      this.#updateFacets();
      return;
    }

    this.#metadataController?.abort();
    const metadataController = new AbortController();
    this.#metadataController = metadataController;
    this.#metadataReady = false;

    const showInitialFacetSkeleton = !this.#hasLoadedMetadata;
    if (showInitialFacetSkeleton) {
      this.dataset.facetsLoading = 'true';
    }

    const products = [];
    let nextUrl = new URL(baseUrl);
    nextUrl.searchParams.delete('page');
    nextUrl.searchParams.set('page', '1');

    try {
      let guard = 0;

      while (nextUrl && guard < 100) {
        guard += 1;

        const documentFragment = await this.#fetchSection(
          nextUrl,
          metadataController,
          this.metadataSectionId
        );

        const payloadNode = documentFragment.querySelector('[data-clay-archive-data]');

        if (!payloadNode) {
          throw new Error('Clay archive metadata payload was not found.');
        }

        const payload = JSON.parse(payloadNode.textContent || '{}');

        if (Array.isArray(payload.products)) {
          products.push(...payload.products);
        }

        if (payload.next) {
          nextUrl = new URL(payload.next, window.location.origin);
        } else {
          nextUrl = null;
        }
      }

      this.#metadata = products.map((product) => this.#withVisibleCategories(product));
      this.#metadataMap = new Map(this.#metadata.map((product) => [String(product.id), product]));
      this.#metadataKey = key;
      this.#metadataReady = true;
      this.#hasLoadedMetadata = true;

      this.#updateLoadedItemsFromMetadata();
      this.#updatePriceFloorFromMetadata();
      this.#restoreClientFilterControls();
      this.#applyClientFilters();
      this.#ensureEnoughVisibleProducts();
      this.#renderBrandDebug();
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('[Clay product archive metadata]', error);
        this.#metadata = this.#metadataFromLoadedItems();
        this.#metadataMap = new Map(
          this.#metadata.map((product) => [String(product.id), product])
        );
        this.#metadataReady = this.#metadata.length > 0;
        this.#updateFacets();
        this.#renderBrandDebug(error);
      }
    } finally {
      if (this.#metadataController === metadataController && showInitialFacetSkeleton) {
        delete this.dataset.facetsLoading;
      }
    }
  }

  #metadataFromLoadedItems() {
    const result = [];

    this.querySelectorAll('[data-archive-grid] > .clay-archive-grid__item').forEach((item) => {
      result.push({
        id: String(item.dataset.productId || ''),
        handle: item.dataset.productHandle || '',
        priceCents: Number(item.dataset.productPrice || 0),
        stock: item.dataset.stockStatus || 'out_of_stock',
        brand: item.dataset.brandName || '',
        brandId: item.dataset.brandId || '',
        vendor: item.dataset.productVendor || '',
        tags: (item.dataset.productTags || '').split('||').filter(Boolean),
        categories: (item.dataset.categoryHandles || '')
          .split('|')
          .filter(Boolean)
          .map((handle) => ({ handle, title: handle }))
          .filter((category) => !this.#isHiddenCategory(category)),
      });
    });

    return result;
  }

  #updateLoadedItemsFromMetadata() {
    this.querySelectorAll('[data-archive-grid] > .clay-archive-grid__item').forEach((item) => {
      const meta = this.#metadataMap.get(String(item.dataset.productId || ''));
      if (!meta) return;

      item.dataset.stockStatus = meta.stock || 'out_of_stock';
      item.dataset.brandName = meta.brand || '';
      item.dataset.brandId = meta.brandId || '';
      item.dataset.productVendor = meta.vendor || item.dataset.productVendor || '';
      item.dataset.productPrice = String(meta.priceCents || 0);
      item.dataset.productTags = (meta.tags || []).join('||');
      item.dataset.categoryHandles = `|${(meta.categories || []).map((category) => category.handle).join('|')}|`;
    });
  }

  #metadataInArchiveScope() {
    return this.#metadata.filter((meta) => {
      if (this.hideUnavailable) {
        if (meta.stock === 'out_of_stock' || Number(meta.priceCents || 0) <= 0) {
          return false;
        }
      }

      if (
        this.requiredProductTag &&
        !this.#hasExactTag(meta.tags || [], this.requiredProductTag)
      ) {
        return false;
      }

      if (this.mode === 'brand') {
        const currentBrand = this.#normalise(this.brandName);
        const productVendor = this.#normalise(meta.vendor || '');
        const productBrand = this.#normalise(meta.brand || '');

        if (currentBrand && currentBrand !== productVendor && currentBrand !== productBrand) {
          return false;
        }
      }

      if (this.mode === 'category' && this.categoryScopeHandles.size > 0) {
        const hasCurrentCategory = (meta.categories || []).some((category) =>
          this.categoryScopeHandles.has(category.handle)
        );

        if (!hasCurrentCategory) return false;
      }

      return true;
    });
  }

  #metaMatches(meta, {
    ignoreAvailability = false,
    ignoreCategories = false,
    ignoreBrands = false,
    ignoreTags = false,
  } = {}) {
    if (!ignoreAvailability && this.#availabilityMode !== 'all') {
      if (meta.stock !== this.#availabilityMode) return false;
    }

    if (!ignoreCategories && this.#selectedCategories.size > 0) {
      const categoryHandles = new Set(
        (meta.categories || []).map((category) => category.handle)
      );

      if (![...this.#selectedCategories].some((handle) => categoryHandles.has(handle))) {
        return false;
      }
    }

    if (!ignoreBrands && this.#selectedBrands.size > 0) {
      if (![...this.#selectedBrands].some(
        (brand) => this.#normalise(brand) === this.#normalise(meta.brand)
      )) {
        return false;
      }
    }

    if (!ignoreTags && this.#selectedTags.size > 0) {
      if (![...this.#selectedTags].some((tag) => this.#hasExactTag(meta.tags || [], tag))) {
        return false;
      }
    }

    return true;
  }

  #updateFacets() {
    if (!this.#metadataReady) {
      return;
    }

    const base = this.#metadataInArchiveScope();
    const statusCounts = { all: 0, in_stock: 0, preorder: 0, out_of_stock: 0 };
    const categoryCounts = new Map();
    const categoryTitles = new Map();
    const brandCounts = new Map();
    const brandTitles = new Map();
    const tagCounts = new Map();

    base.forEach((meta) => {
      if (this.#metaMatches(meta, { ignoreAvailability: true })) {
        statusCounts.all += 1;
        if (meta.stock in statusCounts) statusCounts[meta.stock] += 1;
      }

      if (this.#metaMatches(meta, { ignoreCategories: true })) {
        (meta.categories || [])
          .filter((category) => !this.#isHiddenCategory(category))
          .forEach((category) => {
            const title = category.title || this.#categoryLabelCache.get(category.handle) || category.handle;
            categoryTitles.set(category.handle, title);
            this.#categoryLabelCache.set(category.handle, title);
            categoryCounts.set(
              category.handle,
              (categoryCounts.get(category.handle) || 0) + 1
            );
          });
      }

      if (meta.brand && this.#metaMatches(meta, { ignoreBrands: true })) {
        const key = this.#normalise(meta.brand);
        brandTitles.set(key, meta.brand);
        brandCounts.set(key, (brandCounts.get(key) || 0) + 1);
      }

      if (this.#metaMatches(meta, { ignoreTags: true })) {
        (meta.tags || []).forEach((tag) => {
          const key = this.#normalise(tag);
          if (!key) return;
          tagCounts.set(key, (tagCounts.get(key) || 0) + 1);
        });
      }
    });

    this.querySelectorAll('[data-status-count]').forEach((node) => {
      const status = node.dataset.statusCount;
      node.textContent = `(${statusCounts[status] || 0})`;
    });

    this.#updateCategoryOptions(categoryCounts, categoryTitles);
    this.#updateBrandOptions(brandCounts, brandTitles);
    this.#updateTagOptions(tagCounts);
  }

  #updateStatusCountsFromDom() {
    const counts = { all: 0, in_stock: 0, preorder: 0, out_of_stock: 0 };

    this.querySelectorAll('[data-archive-grid] > .clay-archive-grid__item').forEach((item) => {
      if (!this.#itemMatchesArchiveScope(item)) return;
      counts.all += 1;
      const status = item.dataset.stockStatus || 'out_of_stock';
      if (status in counts) counts[status] += 1;
    });

    this.querySelectorAll('[data-status-count]').forEach((node) => {
      const status = node.dataset.statusCount;
      node.textContent = counts[status] ? `(${counts[status]})` : '';
    });
  }

  #updateCategoryOptions(categoryCounts, categoryTitles) {
    const treeHandles = new Set();

    this.querySelectorAll('[data-category-option]:not([data-extra-category])')
      .forEach((option) => {
        const handle = option.dataset.categoryHandle || '';
        const count = categoryCounts.get(handle) || 0;
        const inPath = option.dataset.categoryPath === 'true';
        const selected = this.#selectedCategories.has(handle);
        const countNode = option.querySelector('[data-option-count]');

        treeHandles.add(handle);
        option.hidden = !(count > 0 || inPath || selected);

        if (countNode) {
          countNode.textContent = `(${count})`;
        }
      });

    const extras = this.querySelector('[data-extra-categories]');

    if (!extras) return;

    const existing = new Map();
    extras.querySelectorAll('[data-category-option]').forEach((option) => {
      const handle = option.dataset.categoryHandle || '';
      if (handle) existing.set(handle, option);
    });

    const extraHandles = new Set(
      [...categoryCounts.entries()]
        .filter(([handle, count]) => count > 0 && !treeHandles.has(handle))
        .map(([handle]) => handle)
    );

    this.#selectedCategories.forEach((handle) => {
      if (!treeHandles.has(handle)) extraHandles.add(handle);
    });

    const desiredHandles = new Set(extraHandles);

    [...extraHandles]
      .map((handle) => ({
        handle,
        count: categoryCounts.get(handle) || 0,
        title: categoryTitles.get(handle) || this.#categoryLabelCache.get(handle) || handle,
      }))
      .sort((a, b) => a.title.localeCompare(b.title))
      .forEach((category) => {
        this.#categoryLabelCache.set(category.handle, category.title);

        let option = existing.get(category.handle);
        if (!option) {
          option = this.#createCheckboxOption({
            kind: 'category',
            value: category.handle,
            label: category.title,
            count: category.count,
            checked: this.#selectedCategories.has(category.handle),
            extra: true,
          });
        } else {
          const input = option.querySelector('[data-category-filter]');
          const labelNode = option.querySelector('.clay-archive-filter__label');
          const countNode = option.querySelector('[data-option-count]');

          option.dataset.categoryTitle = category.title;
          if (input) input.checked = this.#selectedCategories.has(category.handle);
          if (labelNode && labelNode.textContent !== category.title) labelNode.textContent = category.title;
          if (countNode) countNode.textContent = `(${category.count})`;
        }

        extras.append(option);
      });

    existing.forEach((option, handle) => {
      if (!desiredHandles.has(handle)) option.remove();
    });
  }

  #updateBrandOptions(brandCounts, brandTitles) {
    const containers = this.querySelectorAll('[data-brand-options]');

    if (!containers.length) return;

    containers.forEach((container) => {
      const existing = new Map();
      container.querySelectorAll('.clay-archive-filter__option').forEach((option) => {
        const input = option.querySelector('[data-brand-filter]');
        if (input) existing.set(this.#normalise(input.value), option);
      });

      const brandKeys = new Set(
        [...brandCounts.entries()]
          .filter(([, count]) => count > 0)
          .map(([key]) => key)
      );

      this.#selectedBrands.forEach((brand) => brandKeys.add(this.#normalise(brand)));
      const desiredKeys = new Set(brandKeys);

      [...brandKeys]
        .map((key) => ({
          key,
          value: brandTitles.get(key) || [...this.#selectedBrands].find(
            (selected) => this.#normalise(selected) === key
          ) || key,
          count: brandCounts.get(key) || 0,
        }))
        .sort((a, b) => a.value.localeCompare(b.value))
        .forEach((brand) => {
          let option = existing.get(brand.key);

          if (!option) {
            option = this.#createCheckboxOption({
              kind: 'brand',
              value: brand.value,
              label: brand.value,
              count: brand.count,
              checked: [...this.#selectedBrands].some(
                (selected) => this.#normalise(selected) === brand.key
              ),
            });
          } else {
            const input = option.querySelector('[data-brand-filter]');
            const labelNode = option.querySelector('.clay-archive-filter__label');
            const countNode = option.querySelector('[data-option-count]');

            if (input) {
              input.value = brand.value;
              input.checked = [...this.#selectedBrands].some(
                (selected) => this.#normalise(selected) === brand.key
              );
            }
            if (labelNode && labelNode.textContent !== brand.value) labelNode.textContent = brand.value;
            if (countNode) countNode.textContent = `(${brand.count})`;
          }

          container.append(option);
        });

      existing.forEach((option, key) => {
        if (!desiredKeys.has(key)) option.remove();
      });
    });
  }

  #updateTagOptions(tagCounts) {
    this.querySelectorAll('[data-tag-option]').forEach((option) => {
      const input = option.querySelector('[data-tag-filter]');
      const value = option.dataset.tagValue || input?.value || '';
      const key = this.#normalise(value);
      const count = tagCounts.get(key) || 0;
      const selected = [...this.#selectedTags].some(
        (tag) => this.#normalise(tag) === key
      );
      const countNode = option.querySelector('[data-tag-count], [data-option-count]');

      option.hidden = !(count > 0 || selected);
      if (input) input.checked = selected;
      if (countNode) countNode.textContent = `(${count})`;
    });
  }

  #createCheckboxOption({ kind, value, label, count, checked = false, extra = false }) {
    const wrapper = document.createElement('label');
    const input = document.createElement('input');
    const box = document.createElement('span');
    const text = document.createElement('span');
    const countNode = document.createElement('span');

    wrapper.className = 'clay-archive-filter__option';

    if (kind === 'category') {
      wrapper.classList.add('clay-archive-filter__option--category');
      wrapper.style.setProperty('--category-depth', '0');
      wrapper.dataset.categoryOption = '';
      wrapper.dataset.categoryHandle = value;
      wrapper.dataset.categoryTitle = label;
      wrapper.dataset.categoryPath = 'false';
      if (extra) wrapper.dataset.extraCategory = 'true';
    }

    input.className = 'clay-archive-filter__native-control';
    input.type = 'checkbox';
    input.value = value;
    input.checked = checked;

    if (kind === 'category') input.dataset.categoryFilter = '';
    if (kind === 'brand') input.dataset.brandFilter = '';

    box.className = 'clay-archive-filter__checkbox';
    box.setAttribute('aria-hidden', 'true');

    text.className = 'clay-archive-filter__label';
    text.textContent = label;

    countNode.className = 'clay-archive-filter__count';
    countNode.dataset.optionCount = '';
    countNode.textContent = `(${count})`;

    wrapper.append(input, box, text, countNode);
    return wrapper;
  }

  #setupInfiniteScroll() {
    this.#observer?.disconnect();

    const sentinel = this.querySelector('[data-infinite-sentinel]');

    if (!sentinel || !sentinel.dataset.nextUrl) return;

    this.#observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          this.#loadNextPage();
        }
      },
      {
        root: null,
        rootMargin: '700px 0px',
        threshold: 0,
      }
    );

    this.#observer.observe(sentinel);
  }

  async #loadNextPage() {
    if (this.#loadingMore) return;

    const sentinel = this.querySelector('[data-infinite-sentinel]');
    const grid = this.querySelector('[data-archive-grid]');

    if (!sentinel?.dataset.nextUrl || !grid) return;

    const nextUrl = new URL(sentinel.dataset.nextUrl, window.location.origin);

    this.#loadingMore = true;
    this.#appendInlineSkeletons(grid);

    this.#infiniteController?.abort();
    this.#infiniteController = new AbortController();

    try {
      const documentFragment = await this.#fetchSection(
        nextUrl,
        this.#infiniteController
      );

      const nextGrid = documentFragment.querySelector('[data-archive-grid]');
      const nextSentinel = documentFragment.querySelector('[data-infinite-sentinel]');

      if (!nextGrid) {
        sentinel.dataset.nextUrl = '';
        sentinel.closest('[data-infinite-root]')?.setAttribute('hidden', '');
        return;
      }

      const fragment = document.createDocumentFragment();

      nextGrid.querySelectorAll(':scope > .clay-archive-grid__item').forEach((item) => {
        const productId = item.dataset.productId;

        if (
          productId &&
          grid.querySelector(`[data-product-id="${CSS.escape(productId)}"]`)
        ) {
          return;
        }

        const meta = this.#metadataMap.get(String(productId || ''));

        if (meta) {
          item.dataset.stockStatus = meta.stock || 'out_of_stock';
          item.dataset.brandName = meta.brand || '';
          item.dataset.brandId = meta.brandId || '';
          item.dataset.productVendor = meta.vendor || item.dataset.productVendor || '';
          item.dataset.productPrice = String(meta.priceCents || 0);
          item.dataset.productTags = (meta.tags || []).join('||');
          item.dataset.categoryHandles = `|${(meta.categories || []).map((category) => category.handle).join('|')}|`;
        }

        fragment.append(item);
      });

      grid.append(fragment);

      if (nextSentinel?.dataset.nextUrl) {
        sentinel.dataset.nextUrl = nextSentinel.dataset.nextUrl;
      } else {
        sentinel.dataset.nextUrl = '';
        sentinel.closest('[data-infinite-root]')?.setAttribute('hidden', '');
        this.#observer?.disconnect();
      }

      this.#applyClientFilters({ updateFacets: false });
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('[Clay product archive infinite scroll]', error);
      }
    } finally {
      this.#removeInlineSkeletons(grid);
      this.#loadingMore = false;
      this.#updateClientEmptyState();
      this.#ensureEnoughVisibleProducts();
    }
  }

  #appendInlineSkeletons(grid) {
    this.#removeInlineSkeletons(grid);

    const columns = Math.max(
      1,
      getComputedStyle(grid).gridTemplateColumns.split(/\s+/).filter(Boolean).length
    );
    const visibleItems = [...grid.querySelectorAll(':scope > .clay-archive-grid__item:not([hidden])')];
    const remainder = visibleItems.length % columns;
    const count = remainder === 0 ? columns : (columns - remainder) + columns;

    for (let index = 0; index < count; index += 1) {
      const item = document.createElement('li');
      item.className = 'clay-archive-grid__item clay-archive-grid__item--skeleton';
      item.dataset.inlineSkeleton = '';
      item.setAttribute('aria-hidden', 'true');
      item.innerHTML = `
        <article class="clay-skeleton-card clay-skeleton-card--inline">
          <span class="clay-skeleton-card__media"></span>
          <span class="clay-skeleton-card__eyebrow"></span>
          <span class="clay-skeleton-card__brand"></span>
          <span class="clay-skeleton-card__title"></span>
          <span class="clay-skeleton-card__price"></span>
        </article>
      `;
      grid.append(item);
    }
  }

  #removeInlineSkeletons(grid = this.querySelector('[data-archive-grid]')) {
    grid?.querySelectorAll(':scope > [data-inline-skeleton]').forEach((item) => item.remove());
  }

  #ensureEnoughVisibleProducts() {
    const grid = this.querySelector('[data-archive-grid]');
    const sentinel = this.querySelector('[data-infinite-sentinel]');

    if (!grid || !sentinel?.dataset.nextUrl || this.#loadingMore) return;

    const needsClientFiltering =
      this.hideUnavailable ||
      this.mode === 'brand' ||
      this.#availabilityMode !== 'all' ||
      this.#selectedCategories.size > 0 ||
      this.#selectedBrands.size > 0 ||
      this.#selectedTags.size > 0 ||
      Boolean(this.requiredProductTag) ||
      (
        this.mode === 'category' &&
        (
          this.categoryAggregate ||
          this.#backendUrl?.pathname === this.searchUrl.pathname
        )
      );

    if (!needsClientFiltering) return;

    const visibleCount = grid.querySelectorAll(
      ':scope > .clay-archive-grid__item:not([hidden])'
    ).length;

    if (visibleCount < 12) {
      this.#loadNextPage();
    }
  }

  #pushClientState() {
    const visible = new URL(window.location.href);

    if (this.#searchTerm.length >= 3) {
      visible.searchParams.set('q', this.#searchTerm);
    } else if (this.#searchTerm.length === 0) {
      visible.searchParams.delete('q');
    }

    visible.searchParams.delete('cc_availability');
    visible.searchParams.delete('cc_category');
    visible.searchParams.delete('cc_brand');
    visible.searchParams.delete('cc_tag');

    if (this.#availabilityMode !== 'all') {
      visible.searchParams.set('cc_availability', this.#availabilityMode);
    }

    this.#selectedCategories.forEach((handle) => {
      visible.searchParams.append('cc_category', handle);
    });

    this.#selectedBrands.forEach((brand) => {
      visible.searchParams.append('cc_brand', brand);
    });

    this.#selectedTags.forEach((tag) => {
      visible.searchParams.append('cc_tag', tag);
    });

    window.history.pushState({}, '', visible);
  }

  #syncVisibleUrl(backendUrl) {
    const visible = new URL(window.location.href);
    this.#searchTerm = backendUrl.searchParams.get('q') || this.#searchTerm || '';

    for (const key of [...visible.searchParams.keys()]) {
      if (
        key.startsWith('filter.') ||
        key === 'q' ||
        key === 'type' ||
        key === 'page' ||
        key === 'sort_by'
      ) {
        visible.searchParams.delete(key);
      }
    }

    for (const [key, value] of backendUrl.searchParams.entries()) {
      if (key === 'section_id' || key === 'type' || key === 'page') continue;
      visible.searchParams.append(key, value);
    }

    visible.searchParams.delete('cc_availability');
    visible.searchParams.delete('cc_category');
    visible.searchParams.delete('cc_brand');
    visible.searchParams.delete('cc_tag');

    if (this.#availabilityMode !== 'all') {
      visible.searchParams.set('cc_availability', this.#availabilityMode);
    }

    this.#selectedCategories.forEach((handle) => {
      visible.searchParams.append('cc_category', handle);
    });

    this.#selectedBrands.forEach((brand) => {
      visible.searchParams.append('cc_brand', brand);
    });

    this.#selectedTags.forEach((tag) => {
      visible.searchParams.append('cc_tag', tag);
    });

    window.history.pushState({}, '', visible);
  }

  #updatePriceFloorFromMetadata() {
    const range = this.querySelector('[data-price-range]');

    if (!range || !this.#metadata.length) return;

    const base = this.#metadataInArchiveScope().filter(
      (meta) => Number(meta.priceCents || 0) > 0
    );

    if (!base.length) return;

    const minPrice = Math.min(...base.map((meta) => Number(meta.priceCents) / 100));
    const maxPrice = Math.max(...base.map((meta) => Number(meta.priceCents) / 100));
    const minInput = range.querySelector('[data-price-min-input]');
    const maxInput = range.querySelector('[data-price-max-input]');

    if (!minInput || !maxInput) return;

    const minActive = range.dataset.minActive === 'true';
    const maxActive = range.dataset.maxActive === 'true';

    if (!minActive) {
      range.dataset.rangeMin = String(Math.floor(minPrice));
      minInput.value = String(Math.floor(minPrice));
    }

    if (!maxActive) {
      range.dataset.rangeMax = String(Math.ceil(maxPrice));
      maxInput.value = String(Math.ceil(maxPrice));
    }

    this.#paintPriceRange(range);
  }

  #refreshPriceRanges() {
    this.querySelectorAll('[data-price-range]').forEach((range) => {
      this.#paintPriceRange(range);
    });
  }

  #setPriceFromPointer(range, clientX) {
    if (!range) return;

    const minInput = range.querySelector('[data-price-min-input]');
    const maxInput = range.querySelector('[data-price-max-input]');
    const track = range.querySelector('[data-price-track]');

    if (!minInput || !maxInput || !track) return;

    const rect = track.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    const floor = Number(range.dataset.rangeMin || 0);
    const ceiling = Number(range.dataset.rangeMax || 0);
    const value = floor + ratio * (ceiling - floor);
    const minDistance = Math.abs(value - Number(minInput.value));
    const maxDistance = Math.abs(value - Number(maxInput.value));

    this.#setPriceNumericValue(
      range,
      minDistance <= maxDistance ? 'min' : 'max',
      value
    );
  }

  #setPriceValue(range, role, clientX) {
    const track = range?.querySelector('[data-price-track]');

    if (!range || !track) return;

    const rect = track.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    const floor = Number(range.dataset.rangeMin || 0);
    const ceiling = Number(range.dataset.rangeMax || 0);

    this.#setPriceNumericValue(
      range,
      role,
      floor + ratio * (ceiling - floor)
    );
  }

  #setPriceNumericValue(range, role, rawValue) {
    const minInput = range.querySelector('[data-price-min-input]');
    const maxInput = range.querySelector('[data-price-max-input]');

    if (!minInput || !maxInput) return;

    const floor = Number(range.dataset.rangeMin || 0);
    const ceiling = Number(range.dataset.rangeMax || 0);
    let value = Math.round(Math.max(floor, Math.min(ceiling, rawValue)));

    if (role === 'min') {
      value = Math.min(value, Number(maxInput.value));
      minInput.value = String(value);
      range.dataset.minActive = 'true';
    } else {
      value = Math.max(value, Number(minInput.value));
      maxInput.value = String(value);
      range.dataset.maxActive = 'true';
    }

    this.#paintPriceRange(range);
  }

  #paintPriceRange(range) {
    if (!range) return;

    const minInput = range.querySelector('[data-price-min-input]');
    const maxInput = range.querySelector('[data-price-max-input]');
    const minLabel = range.querySelector('[data-price-min-label]');
    const maxLabel = range.querySelector('[data-price-max-label]');
    const minHandle = range.querySelector('[data-price-handle="min"]');
    const maxHandle = range.querySelector('[data-price-handle="max"]');
    const visual = range.querySelector('[data-price-track]');

    if (!minInput || !maxInput || !visual) return;

    const floor = Number(range.dataset.rangeMin || 0);
    const ceiling = Number(range.dataset.rangeMax || 0);
    const min = Math.max(floor, Number(minInput.value));
    const max = Math.min(ceiling, Number(maxInput.value));
    const span = Math.max(ceiling - floor, 1);
    const minPct = ((min - floor) / span) * 100;
    const maxPct = ((max - floor) / span) * 100;

    visual.style.setProperty('--min-pct', `${Math.max(0, Math.min(100, minPct))}%`);
    visual.style.setProperty('--max-pct', `${Math.max(0, Math.min(100, maxPct))}%`);

    minHandle?.setAttribute('aria-valuemin', String(Math.round(floor)));
    minHandle?.setAttribute('aria-valuemax', String(Math.round(ceiling)));
    minHandle?.setAttribute('aria-valuenow', String(Math.round(min)));
    maxHandle?.setAttribute('aria-valuemin', String(Math.round(floor)));
    maxHandle?.setAttribute('aria-valuemax', String(Math.round(ceiling)));
    maxHandle?.setAttribute('aria-valuenow', String(Math.round(max)));

    const formatter = new Intl.NumberFormat(document.documentElement.lang || 'en', {
      maximumFractionDigits: 0,
    });
    const currency = range.dataset.currency || '';

    if (minLabel) minLabel.textContent = `${currency} ${formatter.format(min)}`;
    if (maxLabel) maxLabel.textContent = `${currency} ${formatter.format(max)}`;
  }

  #schedulePriceCommit(range) {
    clearTimeout(this.#priceCommitTimer);
    this.#priceCommitTimer = window.setTimeout(() => {
      this.#commitPriceRange(range);
    }, 300);
  }

  #commitPriceRange(range) {
    const form = range?.closest('[data-filter-form]');
    if (!form) return;
    this.#replaceResults(this.#buildUrlFromForm(form));
  }

  #itemHasFeaturedTag(item) {
    if (!item) return false;

    const wanted = this.#normalise(this.featuredTagHandle);
    if (!wanted) return false;

    return (item.dataset.productTags || '')
      .split('||')
      .some((tag) => this.#normalise(tag) === wanted);
  }

  async #loadFeatured() {
    const container = this.querySelector('[data-featured-products]');
    const track = container?.querySelector('[data-featured-track]');

    if (!container || !track || !this.featuredEnabled) return;

    if (!['category', 'brand'].includes(this.mode)) {
      container.hidden = true;
      return;
    }

    container.hidden = true;
    track.innerHTML = '';
    this.#featuredIndex = 0;

    this.#featuredController?.abort();
    this.#featuredController = new AbortController();

    let url;

    if (this.mode === 'brand') {
      // /collections/vendors?q=Brand is already the correct brand scope.
      // Shopify's /collections/<handle>/<tag> form does not map cleanly onto
      // the special vendors collection, so Featured is checked from product.tags.
      url = new URL(this.browseUrl);
    } else if (this.mode === 'category' && this.categoryHandle) {
      url = new URL(this.browseUrl);
      url.pathname = `${url.pathname.replace(/\/+$/, '')}/${this.featuredTagHandle}`;
      url.search = '';
    } else {
      url = new URL('/collections/all', window.location.origin);
      url.pathname = `${url.pathname.replace(/\/+$/, '')}/${this.featuredTagHandle}`;
      url.search = '';
    }

    const items = [];
    let nextUrl = url;
    let guard = 0;

    try {
      while (nextUrl && items.length < this.featuredLimit && guard < 50) {
        guard += 1;

        const documentFragment = await this.#fetchSection(
          nextUrl,
          this.#featuredController
        );

        const grid = documentFragment.querySelector('[data-archive-grid]');
        if (!grid) break;

        grid.querySelectorAll(':scope > .clay-archive-grid__item').forEach((item) => {
          if (items.length >= this.featuredLimit) return;
          if (!this.#itemMatchesArchiveScope(item)) return;
          if (this.mode === 'brand' && !this.#itemHasFeaturedTag(item)) return;
          items.push(item);
        });

        const nextSentinel = documentFragment.querySelector('[data-infinite-sentinel]');
        nextUrl = nextSentinel?.dataset.nextUrl
          ? new URL(nextSentinel.dataset.nextUrl, window.location.origin)
          : null;
      }

      if (!items.length) {
        if (this.mode === 'brand' && this.dataset.brandDebugEnabled === 'true') {
          console.warn('[Clay product archive featured] No products tagged', this.featuredTagHandle, 'found for', this.brandName, 'using', this.browseUrl.toString());
        }
        return;
      }

      items.forEach((item, index) => {
        const slide = document.createElement('div');
        const clone = item.cloneNode(true);

        slide.className = 'clay-featured-products__slide';
        slide.dataset.featuredSlide = String(index);

        this.#uniquifyIds(clone, `featured-${this.dataset.sectionId}-${index}`);
        slide.append(clone);
        track.append(slide);
      });

      container.hidden = false;

      const controls = container.querySelector('[data-featured-controls]');
      if (controls) controls.hidden = items.length <= 1;

      this.#paintFeaturedPosition();
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('[Clay product archive featured]', error);
      }
    }
  }

  #moveFeatured(direction) {
    const container = this.querySelector('[data-featured-products]');
    const slides = container?.querySelectorAll('[data-featured-slide]');

    if (!slides?.length) return;

    this.#featuredIndex = Math.max(
      0,
      Math.min(slides.length - 1, this.#featuredIndex + direction)
    );

    this.#paintFeaturedPosition();
  }

  #paintFeaturedPosition() {
    const container = this.querySelector('[data-featured-products]');
    const track = container?.querySelector('[data-featured-track]');
    const slides = container?.querySelectorAll('[data-featured-slide]');

    if (!track || !slides?.length) return;

    const rtl = document.documentElement.dir === 'rtl';
    const direction = rtl ? 1 : -1;

    track.style.transform = `translate3d(${direction * this.#featuredIndex * 100}%, 0, 0)`;

    const prev = container.querySelector('[data-featured-prev]');
    const next = container.querySelector('[data-featured-next]');

    if (prev) prev.disabled = this.#featuredIndex === 0;
    if (next) next.disabled = this.#featuredIndex >= slides.length - 1;
  }

  #uniquifyIds(root, suffix) {
    const idMap = new Map();

    root.querySelectorAll('[id]').forEach((node) => {
      const oldId = node.id;
      const newId = `${oldId}-${suffix}`;
      idMap.set(oldId, newId);
      node.id = newId;
    });

    const referenceAttributes = [
      'for',
      'aria-controls',
      'aria-labelledby',
      'aria-describedby',
    ];

    root.querySelectorAll('*').forEach((node) => {
      referenceAttributes.forEach((attribute) => {
        const value = node.getAttribute(attribute);
        if (!value) return;

        node.setAttribute(
          attribute,
          value
            .split(/\s+/)
            .map((token) => idMap.get(token) || token)
            .join(' ')
        );
      });
    });
  }
}

if (!customElements.get('clay-product-archive')) {
  customElements.define('clay-product-archive', ClayProductArchive);
}


/* -------------------------------------------------------------------------- */
/* Archive anchor navigation                                                   */
/* -------------------------------------------------------------------------- */

if (!window.__clayProductArchiveAnchorNavigation) {
  window.__clayProductArchiveAnchorNavigation = true;

  document.addEventListener('click', (event) => {
    const link = event.target.closest?.('a[href^="#"]');
    if (!link) return;

    const href = link.getAttribute('href');
    if (!href || href === '#') return;

    let targetId = href.slice(1);

    try {
      targetId = decodeURIComponent(targetId);
    } catch {
      // Keep the literal fragment if it is not valid URI-encoded text.
    }

    const target = document.getElementById(targetId);

    if (!target?.matches('clay-product-archive')) return;

    event.preventDefault();

    const reducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches;

    target.scrollIntoView({
      behavior: reducedMotion ? 'auto' : 'smooth',
      block: 'start',
    });

    if (window.location.hash !== href) {
      window.history.pushState(null, '', href);
    }
  });
}
